import React, {useEffect, useState} from 'react';
import {StyleSheet} from 'react-native';
import {Heading, Box, Center, VStack} from 'native-base';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import cardValidator from 'card-validator';
import {
  MaskedInput,
  ReadonlyInput,
  ReadonlyTextArea,
} from '../../../../../components/inputs';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/buttons';
import useToast from '../../../../../hooks';
import {message as t} from '../../../../../utils/messages';
import {useContextState} from '../../../../../context/sub-client';
import {isWebApp} from '../../../../../utils/platform/is-web';
import {StackParamList} from '../../../../../navigators';
import InternalTemplate from '../../../../../components/templates/internal';
import {IC2PPaymentForm} from '../../../../../interfaces/forms/c2p-payment-form.interface';
import {SpinnerModal} from '../../../../../components/modals';
import {formatFeeQuote, formatNumber} from '../../../../../utils/format';
import {ICreditCardPaymentForm} from '../../../../../interfaces/forms/credit-card-payment-form.interface';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'PaymentCreditCardFormScreen'
>;

const PaymentCreditCardFormScreen = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    basePaymentC2P,
    fetchBasePaymentC2P,
    invoiceFeeSummary,
    sendPaymentCreditCard,
  } = useContextState();
  const {showInputsError, showC2PError} = useToast();

  const isEditableAmount = () => {
    if (basePaymentC2P) {
      return basePaymentC2P.amountEditable;
    }
    return true;
  };

  const calculateAmount = () => {
    let defaultAmount = 0;

    invoiceFeeSummary?.fees.forEach(fee => {
      defaultAmount += fee.amount.local;
    });

    return defaultAmount;
  };

  const getAmountError = () => {
    return `${t(
      'screen-credit-card-payment-total-amount-error',
    )} ${formatNumber(calculateAmount())}`;
  };

  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {
      number: undefined,
      name: undefined,
      expire: undefined,
      ccv: undefined,
      amount: calculateAmount(),
    } as ICreditCardPaymentForm,
  });

  const loadBasePaymentC2P = async () => {
    setIsLoading(true);
    await fetchBasePaymentC2P();
    setIsLoading(false);
  };

  useEffect(() => {
    if (basePaymentC2P == null) {
      loadBasePaymentC2P();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInvalid: SubmitErrorHandler<IC2PPaymentForm> = async (
    fieldErrors: FieldErrors<IC2PPaymentForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const getFeeListInputValue = () => {
    if (
      invoiceFeeSummary &&
      invoiceFeeSummary.fees &&
      invoiceFeeSummary.fees.length > 0
    ) {
      return invoiceFeeSummary.fees
        .map(fee => formatFeeQuote(fee.dueDate.day, fee.dueDate.month))
        .join(', ');
    }
    return '';
  };

  const getFeeList = () => {
    if (
      invoiceFeeSummary &&
      invoiceFeeSummary.fees &&
      invoiceFeeSummary.fees.length > 0
    ) {
      return invoiceFeeSummary.fees.map(fee => fee.id);
    }
    return [];
  };

  const handleClearForm = () => {
    reset();
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const onSubmit: SubmitHandler<ICreditCardPaymentForm> = async (
    data: ICreditCardPaymentForm,
  ) => {
    try {
      setIsLoading(true);
      const result = await sendPaymentCreditCard({
        ...data,
        invoiceId: invoiceFeeSummary?.id,
        invoiceFees: getFeeList(),
      }); // OJO CAMBIAR
      if (result) {
        navigation.navigate('PaymentCreditCardSuccessScreen');
      }
      setIsLoading(false);
    } catch {
      setIsLoading(false);
      showC2PError();
    }
    return false;
  };

  const checkCreditCardNumber = {
    check: (value: string) => {
      const cardNumber = value.split(' ').join('');
      if (cardNumber.length < 16) {
        return t('credit-card-number-uncomplete');
      }
      const numberValidation = cardValidator.number(cardNumber);
      if (numberValidation.isValid) {
        return true;
      }
      return t('credit-card-number-not-valid');
    },
  };

  const checkCreditCardExpire = {
    check: (value: string) => {
      const expireInfo = value.split('/');
      if (expireInfo.length === 2) {
        const yearValidation = cardValidator.expirationYear(expireInfo[1]);
        const monthValidation = cardValidator.expirationMonth(expireInfo[0]);
        if (yearValidation.isValid && monthValidation.isValid) {
          return true;
        }
      }
      return t('credit-card-expire-date-not-valid');
    },
  };

  const checkCreditCardCCV = {
    check: (value: string) => {
      const ccvValidation = cardValidator.cvv(value);
      if (ccvValidation.isValid) {
        return true;
      }
      return t('credit-card-ccv-not-valid');
    },
  };

  return (
    <InternalTemplate
      title={'screen-credit-card-payment-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
        usage: 'external',
      }}>
      <VStack space={3} justifyContent="center" marginX={4}>
        <SpinnerModal
          show={isLoading}
          title={t('loading-credit-card-payment-send-pay')}
        />
        <Box flex={1}>
          <KeyboardAwareScrollView
            style={styles.content}
            enableOnAndroid={true}
            scrollEnabled={true}
            enableAutomaticScroll={true}
            showsVerticalScrollIndicator={isWebApp}
            extraHeight={100}
            extraScrollHeight={100}
            innerRef={ref => {
              (this as any).scroll = ref;
            }}>
            <Center width="100%" flex={1} justifyContent="flex-start">
              <Box width="100%" py={5}>
                <Heading
                  size="xs"
                  paddingBottom="5"
                  fontWeight="bold"
                  color="#06355E">
                  {t('screen-credit-card-payment-label')}
                </Heading>
                <MaskedInput
                  control={control}
                  name="number"
                  label="screen-credit-card-number-input-label"
                  mask="credit-card-number"
                  error={errors.number}
                  rules={{
                    validate: checkCreditCardNumber,
                  }}
                />
                <MaskedInput
                  control={control}
                  name="name"
                  label="screen-credit-card-fullname-input-label"
                  mask="people-names"
                  error={errors.name}
                  rules={{
                    required: 'form-is-required',
                  }}
                />
                <MaskedInput
                  control={control}
                  name="expire"
                  label="screen-credit-card-expiration-input-label"
                  mask="credit-card-expire"
                  error={errors.expire}
                  rules={{
                    validate: checkCreditCardExpire,
                  }}
                />
                <MaskedInput
                  control={control}
                  name="ccv"
                  label="screen-credit-card-ccv-input-label"
                  mask="credit-card-ccv"
                  error={errors.ccv}
                  rules={{
                    validate: checkCreditCardCCV,
                  }}
                />
                <MaskedInput
                  control={control}
                  name="amount"
                  label="screen-credit-card-amount-input-label"
                  mask="money"
                  error={errors.amount}
                  rules={{
                    required: 'form-is-required',
                    max: {
                      message: getAmountError(),
                      value: calculateAmount(),
                    },
                  }}
                  isEditable={isEditableAmount()}
                />
                <ReadonlyInput
                  label="screen-credit-card-invoice-input-label"
                  value={`${invoiceFeeSummary?.id}`}
                  backgroundColor="#F8F8F8"
                />

                <ReadonlyTextArea
                  label="screen-credit-card-payment-invoice-fees-label"
                  value={getFeeListInputValue()}
                  numberOfLines={5}
                  maxLength={250}
                  backgroundColor="#F8F8F8"
                />

                <Box mt={'5'}>
                  <PrimaryButton
                    label="screen-credit-card-payment-send-label"
                    onPress={handleSubmit(onSubmit, onInvalid)}
                  />
                </Box>
                <Box mt={5} mb={200}>
                  <SecondaryButton
                    label="screen-credit-card-payment-button-cancel-label"
                    onPress={handleClearForm}
                  />
                </Box>
              </Box>
            </Center>
          </KeyboardAwareScrollView>
        </Box>
      </VStack>
    </InternalTemplate>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  file: {
    marginTop: 15,
  },
});

export {PaymentCreditCardFormScreen};
